import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getUserData } from '../redux/slice/slice.auth';
import ENVIRONMENT from '../config/environment';

const FAILED_HTTP_STATUS_CODES = [
	// 4xx: Client Errors
	400, 402, 403, 404, 405, 406, 407, 408, 409, 410, 411, 412, 413, 414, 415, 416,
	417, 418, 421, 422, 423, 424, 425, 426, 428, 429, 431, 451,

	// 5xx: Server Errors
	500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511,
];

class ServiceApi {
	private _axios: AxiosInstance;
	private static _instance: ServiceApi | null = null;

	private constructor(baseUrl: string, token: string | null = null) {
		this._axios = axios.create({
			baseURL: baseUrl,
			validateStatus: () => true,
			responseType: 'json',
			withCredentials: false,
		});

		this._axios.interceptors.request.use(config => {
			if (token) config.headers['Authorization'] = `Bearer ${token}`;
			return config;
		}, Promise.reject);

		this._axios.interceptors.response.use(
			response => {
				if (response.status === 401) {
					localStorage.clear();
					window.location.reload();
				} else if (
					response.status &&
					FAILED_HTTP_STATUS_CODES.includes(response.status)
				) {
					throw new Error(response.data.message.message || response.data.message);
				}
				return response;
			},
			error => {
				if (error.response?.status === 401) {
					localStorage.clear();
					window.location.reload();
				}
				return Promise.reject(error);
			},
		);
	}

	public updateToken(token: string | null) {
		if (token) {
			this._axios.defaults.headers['Authorization'] = `Bearer ${token}`;
		} else {
			delete this._axios.defaults.headers['Authorization'];
		}
	}

	public static destroyInstance() {
		this._instance = null;
	}

	public static getInstance(
		instanceFor?: 'DS' | 'OS',
		baseUrl: string | null = null,
	): ServiceApi {
		const token =
			instanceFor === 'DS' ? ENVIRONMENT.DS_API_TOKEN : getUserData()?.token;
		const resolvedBaseUrl =
			baseUrl ||
			(instanceFor === 'DS'
				? ENVIRONMENT.DS_API_URL_BUYER_CREDIT
				: ENVIRONMENT.API_URL);

		if (
			!this._instance ||
			this._instance._axios.defaults.baseURL !== resolvedBaseUrl ||
			this._instance._axios.defaults.headers['Authorization'] !== `Bearer ${token}`
		) {
			this._instance = new ServiceApi(resolvedBaseUrl, token);
		}

		if (this._instance && token) {
			this._instance.updateToken(token);
		}

		return this._instance;
	}

	public GET(url: string, config?: AxiosRequestConfig) {
		return this._axios.get(url, config);
	}

	public POST(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.post(url, data, config);
	}

	public PUT(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.put(url, data, config);
	}

	public PATCH(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.patch(url, data, config);
	}

	public DELETE(url: string, config?: AxiosRequestConfig) {
		return this._axios.delete(url, config);
	}

	public Axios(url: string, config: AxiosRequestConfig) {
		return this._axios.request({ url, ...config });
	}
}

export default ServiceApi;
